import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import {
  Document,
  DOCUMENT_STATUS_TEXT_CLASSES,
  getDocumentTypeDisplay,
  getDocumentUserTypeDisplay,
} from '../../../../../../../models/document-template';
import { DocumentTemplateService } from '../../../../../../../services/document-template/document-template.service';
import { CallbackResult, CallbackResultStatus, dateStringFromISO8601, timeStringFromISO8601 } from '../../../../../../../utils';


@Component({
  selector: 'app-manage-document-modal',
  templateUrl: './manage-document-modal.component.html',
  styleUrls: ['./manage-document-modal.component.css']
})
export class ManageDocumentModalComponent implements OnInit {
  @Input() documentId: string;
  @Input() savedCallback: (result: CallbackResult) => undefined;

  public document: Document | null = null;

  public loadingDocument = true;
  public loadingSubmit = false;
  public errorMessage = '';
  public serverError: number = null;

  public getDocumentTypeDisplay = getDocumentTypeDisplay;
  public getDocumentUserTypeDisplay = getDocumentUserTypeDisplay;
  public timeStringFromISO8601 = timeStringFromISO8601;
  public dateStringFromISO8601 = dateStringFromISO8601;

  constructor(
    public activeModal: NgbActiveModal,
    private documentTemplateService: DocumentTemplateService
  ) {}

  ngOnInit() {
    this.loadDocument();
  }

  public isLoading() {
    return this.loadingDocument || this.loadingSubmit;
  }

  public hasSubmit() {
    return false;
  }

  public getStatusText(): string {
    if (!this.document.is_complete) {
      return 'Incomplete';
    } else if (this.document.is_pending_staff_approval) {
      return 'Pending Review';
    } else if (this.document.is_denied_staff_approval) {
      return 'Denied Approval'
    } else if (this.document.is_expired_for_all) {
      return 'Expired';
    } else if (this.document.is_invalid) {
      return 'Invalid';
    } else {
      return 'Valid';
    }
  }

  public getStatusClass(): string {
    if (!this.document.is_complete) {
      return DOCUMENT_STATUS_TEXT_CLASSES.incomplete;
    } else if (this.document.is_pending_staff_approval) {
      return DOCUMENT_STATUS_TEXT_CLASSES.pending;
    } else if (this.document.is_denied_staff_approval) {
      return DOCUMENT_STATUS_TEXT_CLASSES.denied;
    } else if (this.document.is_expired_for_all) {
      return DOCUMENT_STATUS_TEXT_CLASSES.expired;
    } else if (this.document.is_invalid) {
      return DOCUMENT_STATUS_TEXT_CLASSES.invalid;
    } else {
      return DOCUMENT_STATUS_TEXT_CLASSES.valid;
    }
  }

  public markAsValidAvailable(): boolean {
    return (
      !this.isLoading() &&
      this.document.is_complete &&
      !this.document.is_pending_staff_approval &&
      !this.document.is_denied_staff_approval &&
      !this.document.is_expired_for_all &&
      this.document.is_invalid
    );
  }

  public markAsInvalidAvailable(): boolean {
    return (
      !this.isLoading() &&
      this.document.is_complete &&
      !this.document.is_pending_staff_approval &&
      !this.document.is_denied_staff_approval &&
      !this.document.is_expired_for_all &&
      !this.document.is_invalid
    );
  }

  public handleChangeValidity(isValid: boolean) {
    this.loadingSubmit = true;

    let submitPromise: Promise<void>;
    if (isValid) {
      submitPromise = this.documentTemplateService.markDocumentAsValid(this.document.id);
    } else {
      submitPromise = this.documentTemplateService.markDocumentAsInvalid(this.document.id);
    }

    submitPromise
      .then(() => {
        this.savedCallback({
          text: 'Document marked as ' + (isValid ? 'valid' : 'invalid'),
          status: CallbackResultStatus.success
        });
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to change this document.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  private loadDocument() {
    this.loadingDocument = true;

    this.documentTemplateService
      .getDocument(this.documentId)
      .then(result => {
        this.document = result;
      })
      .catch()
      .finally(() => { this.loadingDocument = false; });
  }
}
